import { useState, useEffect, useRef } from "react";
import { Users, Lock, Mail, Shield, Edit, X, Camera, Save, Loader2, MapPin, Phone } from "lucide-react";

interface UserListItem {
  id: number;
  name: string;
  email: string;
  role_name: string;
  profile_image: string | null;
}

const UserData = () => {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState<any>({});
  const [password, setPassword] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const token = localStorage.getItem("sevaa_admin_token");
  // const backendUrl = "http://localhost:1415";
  // const backendUrl = "http://localhost/seva-backend";
  const backendUrl = "https://www.sevaafoundation.com/seva-backend";

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/managed_users.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (response.status === 403) {
        setAccessDenied(true);
      } else if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openUserModal = async (userId: number) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
    setIsFetchingDetails(true);
    setMessage({ text: "", type: "" });
    setPassword("");
    setImageFile(null);

    try {
      const response = await fetch(`${backendUrl}/api/auth/profile.php?id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setFormData(data.profile);
        setPreviewImage(data.profile.profile_image ? `${backendUrl}${data.profile.profile_image}` : null);
      }
    } catch (error) {
      console.error("Error fetching user details", error);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUserId(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ text: "", type: "" });

    const dataToSend = new FormData();
    dataToSend.append("name", formData.name || "");
    dataToSend.append("email", formData.email || "");
    if (password) dataToSend.append("password", password);
    dataToSend.append("phone", formData.phone || "");
    dataToSend.append("street_address", formData.street_address || "");
    dataToSend.append("city", formData.city || "");
    dataToSend.append("state", formData.state || "");
    dataToSend.append("zip_code", formData.zip_code || "");
    dataToSend.append("country", formData.country || "");
    
    if (!imageFile && formData.profile_image) {
        dataToSend.append("existing_image", formData.profile_image);
    } else if (imageFile) {
        dataToSend.append("profile_image", imageFile);
    }

    try {
      const response = await fetch(`${backendUrl}/api/auth/profile.php?id=${selectedUserId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: dataToSend
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ text: "User profile updated successfully!", type: "success" });
        setPassword("");
        fetchUsers(); // Refresh the list to reflect any name/image changes
      } else {
        setMessage({ text: data.error || "Failed to update", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Network error occurred", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // UI 1: Access Denied Screen
  if (accessDenied) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-24 h-24 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 shadow-sm">
          <Lock size={48} />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-3">Access Denied</h1>
        <p className="text-slate-500 max-w-md">You do not have the required administrative privileges to view or manage other users' data.</p>
      </div>
    );
  }

  // UI 2: Loading State
  if (isLoading) return <div className="p-8 text-center text-slate-500">Loading user data...</div>;

  // UI 3: Main List Screen
  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Data Management</h1>
          <p className="text-slate-500 text-sm">View and edit details of users below your hierarchy.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-sm">
              <th className="p-4 font-semibold">User Profile</th>
              <th className="p-4 font-semibold">Email</th>
              <th className="p-4 font-semibold">Role</th>
              <th className="p-4 font-semibold text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <td className="p-4 flex items-center gap-3">
                  {user.profile_image ? (
                    <img src={`${backendUrl}${user.profile_image}`} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold">
                      {user.name.charAt(0)}
                    </div>
                  )}
                  <span className="font-medium text-slate-800">{user.name}</span>
                </td>
                <td className="p-4 text-slate-600">
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-slate-400" /> {user.email}
                  </div>
                </td>
                <td className="p-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium border border-slate-200">
                    <Shield size={14} /> {user.role_name}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <button onClick={() => openUserModal(user.id)} className="px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                    <Edit size={14} /> Edit Data
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-slate-500">No manageable users found in your hierarchy.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* POPUP MODAL FOR EDITING */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-900 text-white">
              <h3 className="text-lg font-bold">Edit User Details</h3>
              <button onClick={closeModal} className="p-1 hover:bg-slate-800 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {isFetchingDetails ? (
                <div className="py-12 flex justify-center"><Loader2 size={32} className="animate-spin text-orange-500" /></div>
              ) : (
                <form onSubmit={handleUpdateSubmit} className="space-y-6">
                  
                  {message.text && (
                    <div className={`p-4 rounded-xl text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                      {message.text}
                    </div>
                  )}

                  <div className="flex items-center gap-6 pb-6 border-b border-slate-100">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-slate-200 overflow-hidden flex items-center justify-center">
                        {previewImage ? <img src={previewImage} alt="Profile" className="w-full h-full object-cover" /> : <Users size={32} className="text-slate-400" />}
                      </div>
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 p-1.5 bg-orange-500 text-white rounded-full hover:bg-orange-600 shadow-md">
                        <Camera size={14} />
                      </button>
                      <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{formData.name}</h4>
                      <p className="text-slate-500 text-sm flex items-center gap-1"><Shield size={14}/> {formData.role_name}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Full Name</label>
                      <input type="text" value={formData.name || ""} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                  <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
                      <input 
                        type="email" 
                        value={formData.email || ""} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Phone</label>
                      <input type="text" value={formData.phone || ""} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">New Password (Optional)</label>
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                  </div>

                  <h4 className="font-bold text-slate-800 border-b border-slate-100 pb-2 mt-4 flex items-center gap-2"><MapPin size={18} className="text-orange-500"/> Address Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Street Address</label>
                      <input type="text" value={formData.street_address || ""} onChange={(e) => setFormData({...formData, street_address: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">City</label>
                      <input type="text" value={formData.city || ""} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">State / Province</label>
                      <input type="text" value={formData.state || ""} onChange={(e) => setFormData({...formData, state: e.target.value})} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-orange-500" />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                    <button type="button" onClick={closeModal} className="px-5 py-2.5 text-slate-600 font-semibold hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
                    <button type="submit" disabled={isSaving} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 py-2.5 rounded-xl transition-all flex items-center gap-2">
                      {isSaving ? <><Loader2 size={18} className="animate-spin" /> Saving...</> : <><Save size={18} /> Update User</>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserData;